<script lang="ts">
  export let title: string = '';
  export let actionText: string = '';
  export let actionHref: string = '';
  export let columns: Array<{
    key: string;
    label: string;
    width?: string;
    align?: string;
    mobilePrimary?: boolean;
    hideOnMobile?: boolean;
    render?: (value: any, row: any) => string;
  }> = [];
  export let data: Array<Record<string, any>> = [];
  export let onActionClick: (() => void) | null = null;
  export let onRowClick: ((row: any) => void) | null = null;
  export let loading: boolean = false;

  $: primaryColumn = columns.find((column) => column.mobilePrimary) ?? columns[0];
  $: detailColumns = columns.filter(
    (column) => column.key !== primaryColumn?.key && !column.hideOnMobile
  );

  function handleActionClick() {
    if (onActionClick) {
      onActionClick();
    }
  }

  function handleRowClick(event: MouseEvent, row: any) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.tagName === 'A' || target.closest('button') || target.closest('a')) {
      return;
    }

    if (onRowClick) {
      onRowClick(row);
    }
  }

  function handleCardActivate(row: any) {
    if (onRowClick) {
      onRowClick(row);
    }
  }
</script>

<div class="table-container">
  {#if title || actionText}
    <div class="table-header" class:action-only={!title && actionText}>
      {#if title}
        <h3 class="table-title">{title}</h3>
      {/if}
      {#if actionText}
        {#if actionHref}
          <a href={actionHref} class="table-action">{actionText}</a>
        {:else}
          <button type="button" on:click={handleActionClick} class="table-action">{actionText}</button>
        {/if}
      {/if}
    </div>
  {/if}

  <div class="table-wrapper">
    {#if loading}
      <div class="table-loading" role="status" aria-live="polite">Loading...</div>
    {:else}
      <table class="data-table desktop-table">
        <thead>
          <tr>
            {#each columns as column}
              <th style={`${column.width ? `width: ${column.width};` : ''}${column.align ? ` text-align: ${column.align};` : ''}`}>{column.label}</th>
            {/each}
          </tr>
        </thead>
        <tbody>
          {#each data as row}
            <tr class:clickable={!!onRowClick} on:click={(e) => handleRowClick(e, row)}>
              {#each columns as column}
                <td style={column.align ? `text-align: ${column.align};` : ''}>
                  {#if column.render}
                    {@html column.render(row[column.key], row)}
                  {:else}
                    {row[column.key] || '—'}
                  {/if}
                </td>
              {/each}
            </tr>
          {/each}
        </tbody>
      </table>

      <div class="mobile-card-list" aria-label={title || 'Data list'}>
        {#each data as row}
          {#if onRowClick}
            <button
              type="button"
              class="mobile-card clickable"
              on:click={() => handleCardActivate(row)}
            >
              {#if primaryColumn}
                <div class="mobile-card-primary">
                  {#if primaryColumn.render}
                    {@html primaryColumn.render(row[primaryColumn.key], row)}
                  {:else}
                    {row[primaryColumn.key] || '—'}
                  {/if}
                </div>
              {/if}

              {#if detailColumns.length > 0}
                <dl class="mobile-card-fields">
                  {#each detailColumns as column (column.key)}
                    <div class="mobile-card-field">
                      <dt>{column.label}</dt>
                      <dd class:align-center={column.align === 'center'} class:align-right={column.align === 'right'}>
                        {#if column.render}
                          {@html column.render(row[column.key], row)}
                        {:else}
                          {row[column.key] || '—'}
                        {/if}
                      </dd>
                    </div>
                  {/each}
                </dl>
              {/if}
            </button>
          {:else}
            <div class="mobile-card">
              {#if primaryColumn}
                <div class="mobile-card-primary">
                  {#if primaryColumn.render}
                    {@html primaryColumn.render(row[primaryColumn.key], row)}
                  {:else}
                    {row[primaryColumn.key] || '—'}
                  {/if}
                </div>
              {/if}

              {#if detailColumns.length > 0}
                <dl class="mobile-card-fields">
                  {#each detailColumns as column (column.key)}
                    <div class="mobile-card-field">
                      <dt>{column.label}</dt>
                      <dd class:align-center={column.align === 'center'} class:align-right={column.align === 'right'}>
                        {#if column.render}
                          {@html column.render(row[column.key], row)}
                        {:else}
                          {row[column.key] || '—'}
                        {/if}
                      </dd>
                    </div>
                  {/each}
                </dl>
              {/if}
            </div>
          {/if}
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .table-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    overflow: hidden;
    max-width: 100%;
  }

  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
  }

  .table-header.action-only {
    justify-content: flex-end;
  }

  .table-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
    min-width: 0;
  }

  .table-action {
    font-size: 0.875rem;
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
    white-space: nowrap;
  }

  .table-action:hover {
    color: #2563eb;
    text-decoration: underline;
  }

  .table-wrapper {
    overflow-x: auto;
    max-width: 100%;
    min-width: 0;
  }

  .table-loading {
    padding: 2rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .mobile-card-list {
    display: none;
  }

  .data-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    background: white;
  }

  .data-table thead {
    background: #f8fafc;
  }

  .data-table th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .data-table th[style*='px'] {
    min-width: inherit;
  }

  .data-table th[style*='auto'] {
    width: auto;
    min-width: 0;
  }

  .data-table td {
    padding: 1rem;
    font-size: 0.875rem;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
    text-align: left;
  }

  .data-table td:first-child {
    text-align: left !important;
  }

  .data-table tbody tr:hover {
    background-color: #f9fafb;
  }

  .data-table tbody tr.clickable {
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .data-table tbody tr.clickable:hover {
    background-color: #f3f4f6;
  }

  .data-table tbody tr:last-child td {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    .table-header {
      flex-direction: row;
      align-items: center;
      padding: 1rem;
    }

    .table-title {
      font-size: 1rem;
    }

    .desktop-table {
      display: none;
    }

    .mobile-card-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      padding: 1rem;
    }

    .mobile-card {
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 1rem;
      background: #fff;
      text-align: left;
      width: 100%;
      box-sizing: border-box;
    }

    button.mobile-card {
      appearance: none;
      font: inherit;
      color: inherit;
      cursor: pointer;
      transition: background-color 0.2s ease, border-color 0.2s ease;
    }

    button.mobile-card:hover {
      background-color: #f9fafb;
      border-color: #d1d5db;
    }

    button.mobile-card:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.2);
      border-color: var(--color-primary, #ffb400);
    }

    button.mobile-card:active {
      transform: translateY(1px);
    }

    .mobile-card-primary {
      font-size: 1rem;
      font-weight: 600;
      color: #111827;
      margin-bottom: 0.75rem;
      line-height: 1.4;
      word-break: break-word;
    }

    .mobile-card-fields {
      display: grid;
      gap: 0.625rem;
      margin: 0;
    }

    .mobile-card-field {
      display: grid;
      grid-template-columns: minmax(5.5rem, 38%) 1fr;
      gap: 0.75rem;
      align-items: start;
    }

    .mobile-card-field dt {
      margin: 0;
      font-size: 0.75rem;
      font-weight: 500;
      color: #6b7280;
      line-height: 1.4;
    }

    .mobile-card-field dd {
      margin: 0;
      font-size: 0.875rem;
      color: #374151;
      line-height: 1.4;
      word-break: break-word;
    }

    .mobile-card-field dd.align-center {
      text-align: left;
    }

    .mobile-card-field dd.align-right {
      text-align: left;
    }

    .mobile-card :global(.confirmation-badge),
    .mobile-card :global(.status-badge) {
      white-space: normal;
      text-align: left;
      max-width: 100%;
    }

    .mobile-card :global(.company-link) {
      font-size: inherit;
    }
  }
</style>
